import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { CustomRSVPQuestion } from './CustomRSVPBuilder';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

interface CustomRSVPFormProps {
  questions: CustomRSVPQuestion[];
  onSubmit: (answers: Record<string, string | string[]>) => void;
  isPreview?: boolean;
}

export function RSVPFormPreview({ questions, onSubmit, isPreview = false }: CustomRSVPFormProps) {
  const isMobile = useIsMobile();
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [visibleQuestions, setVisibleQuestions] = useState<Set<string>>(new Set());
  const [requiredQuestions, setRequiredQuestions] = useState<Set<string>>(new Set());

  // Initialize visible and required questions
  useEffect(() => {
    const initialVisible = new Set<string>();
    const initialRequired = new Set<string>();

    questions.forEach(question => {
      // By default, questions are visible unless they have conditional logic that hides them
      initialVisible.add(question.id);
      
      if (question.required) {
        initialRequired.add(question.id);
      }
    });

    setVisibleQuestions(initialVisible);
    setRequiredQuestions(initialRequired);
  }, [questions]);

  // Apply conditional logic when answers change
  useEffect(() => {
    const newVisible = new Set<string>();
    const newRequired = new Set<string>();

    questions.forEach(question => {
      let isVisible = true;
      let isRequired = question.required;

      // Check all conditional logic rules for this question
      questions.forEach(triggerQuestion => {
        if (triggerQuestion.conditionalLogic) {
          triggerQuestion.conditionalLogic.forEach(rule => {
            if (rule.targetQuestionIds.includes(question.id)) {
              const triggerAnswer = answers[triggerQuestion.id];
              const shouldApplyRule = Array.isArray(triggerAnswer) 
                ? triggerAnswer.includes(rule.triggerValue)
                : triggerAnswer === rule.triggerValue;

              if (shouldApplyRule) {
                switch (rule.action) {
                  case 'show':
                    isVisible = true;
                    break;
                  case 'hide':
                    isVisible = false;
                    break;
                  case 'required':
                    isRequired = true;
                    break;
                  case 'skip':
                    isVisible = false;
                    break;
                }
              }
            }
          });
        }
      });

      if (isVisible) {
        newVisible.add(question.id);
      }
      if (isRequired && isVisible) {
        newRequired.add(question.id);
      }
    });

    setVisibleQuestions(newVisible);
    setRequiredQuestions(newRequired);
  }, [answers, questions]);

  const updateAnswer = (questionId: string, value: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isPreview) {
      toast.info('This is a preview - form submission is disabled');
      return;
    }

    // Validate required fields
    const missingRequired = questions
      .filter(q => visibleQuestions.has(q.id) && requiredQuestions.has(q.id))
      .filter(q => !answers[q.id] || (Array.isArray(answers[q.id]) && answers[q.id].length === 0))
      .map(q => q.text);

    if (missingRequired.length > 0) {
      toast.error(`Please fill in required fields: ${missingRequired.join(', ')}`);
      return;
    }

    // Filter answers to only include visible questions
    const filteredAnswers = Object.fromEntries(
      Object.entries(answers).filter(([questionId]) => visibleQuestions.has(questionId))
    );

    onSubmit(filteredAnswers);
  };

  const renderQuestion = (question: CustomRSVPQuestion) => {
    if (!visibleQuestions.has(question.id)) {
      return null;
    }

    const isRequired = requiredQuestions.has(question.id);
    const currentAnswer = answers[question.id];

    return (
      <div key={question.id} className="space-y-2">
        <Label className="flex items-center gap-2 text-sm md:text-base">
          {question.text}
          {isRequired && <span className="text-destructive">*</span>}
        </Label>
        
        {question.helpText && (
          <p className="text-xs md:text-sm text-muted-foreground">{question.helpText}</p>
        )}

        {question.type === 'text' && (
          <Input
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
            disabled={isPreview}
            className="text-sm md:text-base h-10 md:h-11"
          />
        )}

        {question.type === 'textarea' && (
          <Textarea
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
            disabled={isPreview}
            className="text-sm md:text-base min-h-[80px] md:min-h-[100px]"
          />
        )}

        {question.type === 'email' && (
          <Input
            type="email"
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
            disabled={isPreview}
            className="text-sm md:text-base h-10 md:h-11"
          />
        )}

        {question.type === 'phone' && (
          <Input
            type="tel"
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
            disabled={isPreview}
            className="text-sm md:text-base h-10 md:h-11"
          />
        )}

        {question.type === 'number' && (
          <Input
            type="number"
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
            disabled={isPreview}
            className="text-sm md:text-base h-10 md:h-11"
          />
        )}

        {question.type === 'date' && (
          <Input
            type="date"
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
            disabled={isPreview}
          />
        )}

        {question.type === 'time' && (
          <Input
            type="time"
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
            disabled={isPreview}
          />
        )}

        {question.type === 'select' && question.options && (
          <Select
            value={currentAnswer as string || ''}
            onValueChange={(value) => updateAnswer(question.id, value)}
            disabled={isPreview}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {question.options.map((option, index) => (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {question.type === 'radio' && question.options && (
          <RadioGroup
            value={currentAnswer as string || ''}
            onValueChange={(value) => updateAnswer(question.id, value)}
            disabled={isPreview}
          >
            {question.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`${question.id}-${index}`} />
                <Label htmlFor={`${question.id}-${index}`}>{option}</Label>
              </div>
            ))}
          </RadioGroup>
        )}

        {(question.type === 'checkbox' || question.type === 'multiselect') && question.options && (
          <div className="space-y-2">
            {question.options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.id}-${index}`}
                  checked={Array.isArray(currentAnswer) ? currentAnswer.includes(option) : false}
                  onCheckedChange={(checked) => {
                    const currentValues = Array.isArray(currentAnswer) ? currentAnswer : [];
                    if (checked) {
                      updateAnswer(question.id, [...currentValues, option]);
                    } else {
                      updateAnswer(question.id, currentValues.filter(v => v !== option));
                    }
                  }}
                  disabled={isPreview}
                />
                <Label htmlFor={`${question.id}-${index}`}>{option}</Label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg md:text-xl">
          {isPreview ? 'Form Preview' : 'RSVP Form'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
          {questions.map(renderQuestion)}
          
          {!isPreview && (
            <Button 
              type="submit" 
              className="w-full h-11 md:h-12 text-sm md:text-base"
            >
              Submit RSVP
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  );
}